/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import JSZip from 'jszip';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AutomationsService {
  /**
   * HTTP options for the requests
   */
  private httpOptions: any = {
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    }),
  };

  /**
   * URL for the API
   */
  private urlApi = environment.url_api;

  /**
   * Event source generation files
   */
  private eventSource: EventSource | undefined;

  /**
   * Constructor for the AutomationsService class
   * @param http HttpClient
   */
  constructor(private http: HttpClient) {}

  /**
   * Get all automations
   * @param page - The page number for pagination
   * @param filter - Optional filter object for searching and sorting
   * @returns An observable containing the automations data
   **/
  getAllAutomations(page: number, filter?: any, collaboratorId?: string): Observable<any> {
    const sortField = filter?.sortField || 'updated_at';
    const sortOrder = Number(filter?.sortOrder) > 0 ? 'asc' : 'desc';
    let URI: string =
      this.urlApi +
      'automations' +
      (collaboratorId ? '/collaborators/' + collaboratorId : '') +
      '?page=' +
      (page + 1) +
      '&limit=10&sort={"' +
      sortField +
      '":"' +
      sortOrder +
      '"}';

    if (filter?.searchValue !== '' && filter?.searchValue !== undefined && filter?.searchValue !== null) {
      URI = URI + '&filters={"name":{"regex":"' + filter.searchValue + '"}}';
    }

    return this.http.get(URI, this.httpOptions);
  }

  /**
   * Get automation by id
   * @param id - The ID of the automation to retrieve
   * @returns An observable containing the automation data
   **/
  getAutomationById(id: string): Observable<any> {
    const URI: string = this.urlApi + 'automations/' + id;

    return this.http.get(URI, this.httpOptions);
  }

  /**
   * Create automation
   * @param data - The data to create the automation with
   * @returns An observable containing the created automation data
   **/
  createAutomation(data: any): Observable<any> {
    const URI: string = this.urlApi + 'automations';

    return this.http.post(URI, data, this.httpOptions);
  }

  /**
   * Update automation
   * @param id - The ID of the automation to update
   * @param data - The data to update the automation with
   * @returns An observable containing the updated automation data
   **/
  updateAutomation(id: string, data: any): Observable<any> {
    const URI: string = this.urlApi + 'automations/' + id;

    return this.http.put(URI, data, this.httpOptions);
  }

  /**
   * Get atomation form
   * @param automationId
   */
  getAutomationForm(automationId: string): Observable<any> {
    const URI = this.urlApi + 'forms/criteria?filters={"automation":{"=":"' + automationId + '"}}';

    return this.http.get(URI, this.httpOptions);
  }

  /**
   * Clone or transfer automation
   * @param automationId - The ID of the automation to clone or transfer
   * @param newOwner - Optional new owner for the automation
   * @returns An observable containing the result of the clone or transfer operation
   **/
  cloneOrTransferAutomation(automationId: string, newOwner?: string): Observable<any> {
    const URI = this.urlApi + 'automations/clone-automation';
    const dataDocument = {
      automationId,
      newOwner,
    };

    return this.http.post(URI, dataDocument);
  }

  /**
   * Delete automation
   * @param id - The ID of the automation to delete
   * @returns An observable containing the result of the delete operation
   **/
  deleteAutomation(id: string): Observable<any> {
    const URI = this.urlApi + 'automations/' + id;

    return this.http.delete(URI, this.httpOptions);
  }

  /**
   * Deletes a specific template from an automation.
   * @param automationId - The ID of the automation
   * @param templateId - The ID of the template to delete
   * @returns An observable containing the result of the delete operation
   */
  deleteTemplateAutomation(automationId: string, templateId: string): Observable<any> {
    const URI = this.urlApi + 'automations/' + automationId + '/templates/' + templateId;

    return this.http.delete(URI, this.httpOptions);
  }

  /**
   * Generate excel templates
   * @param templates - Array templates to excel generate
   * @returns An observable containing the result of the post operation
   */
  generateExcelTemplates(templates: string[]): Observable<any> {
    const URI = this.urlApi + 'convert/templates/excel';

    return this.http.post(URI, { templates }, this.httpOptions);
  }

  /**
   * Form submission
   * @param data
   * @param typeDataForm
   * @returns An observable containing the result of the post operation
   */
  documentsGenerate(data: any, typeDataForm: string, uuid: string): Observable<any> {
    const URI = this.urlApi + 'form-logs/' + typeDataForm + '/form-submission/' + uuid;

    return this.http.post(URI, data, this.httpOptions);
  }

  /**
   * Update form
   * @param id
   * @param data
   * @returns An observable containing the result of the patch operation
   */
  updateForm(id: string, data: any): Observable<any> {
    const URI = this.urlApi + 'form/update/' + id;

    return this.http.put(URI, data, this.httpOptions);
  }

  /**
   * Massive update variables
   * @param data
   * @returns An observable containing the result of the patch operation
   */
  massiveUpdateVariables(data: any): Observable<any> {
    const URI = this.urlApi + 'variables/bulk';

    return this.http.patch(URI, data, this.httpOptions);
  }

  /**
   * File generation listener service
   * @param uuid
   */
  fileGenerationListener(uuid: any): Observable<any> {
    const URI = this.urlApi + 'convert/listener/file-generation/' + uuid;

    return new Observable<any>(observer => {
      this.eventSource = new EventSource(URI);

      this.eventSource.onmessage = event => {
        const data = JSON.parse(event.data);
        observer.next(data);
      };
    });
  }

  /**
   * Stop file generation listener service
   */
  stopFileGenerationListener() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = undefined;
    }
  }

  createFile(base64: any, extension: string): string {
    if (base64.pdf && extension === '.pdf') {
      return `data:application/pdf;base64,${base64.pdf}`;
    }

    if (base64.docx && extension === '.docx') {
      return `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${base64.docx}`;
    }

    return '';
  }

  createZipFile(arrayFiles: any[]): Promise<Blob> {
    const zip = new JSZip();

    arrayFiles.forEach((file, index) => {
      const nameFile = file.attributes.name
        .trim()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\W/g, '_');
      if (file.attributes.base64.pdf) {
        const base64DataPDF = `data:application/pdf;base64,${file.attributes.base64.pdf}`;
        const bytes = this.base64ToBytes(base64DataPDF);
        zip.file(`${nameFile}_${index + 1}.pdf`, bytes);
      }

      if (file.attributes.base64.docx) {
        const base64DataDOCX = `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${file.attributes.base64.docx}`;
        const bytes = this.base64ToBytes(base64DataDOCX);
        zip.file(`${nameFile}_${index + 1}.docx`, bytes);
      }
    });

    return zip.generateAsync({ type: 'blob' });
  }

  private base64ToBytes(base64String: string): Uint8Array {
    const base64Data = base64String.split(',')[1]; // Eliminar el prefijo "data:application/...;base64,"
    const binaryString = window.atob(base64Data); // Decodificar a una cadena binaria
    const len = binaryString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; ++i) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }
}
